import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
  Rectangle,
} from 'recharts';
import type { MonthlyCombatPower } from '../../../types/combat-power.js';

export interface ChartItem {
  label: string;
  actual: number;
  actualInProgress: number;
  currentTarget: number;
  target: number;
  teamContext?: string; // PDGまたはACE — バー色の判定用
}

/** @graph-connects none */
export const TARGET_OUTLINE_COLOR = '#9ca3af';
/** @graph-connects none */
export const CURRENT_TARGET_COLOR = '#d1d5db';
/** @graph-connects none */
export const PDG_COLOR = '#eab308'; // yellow
/** @graph-connects none */
export const ACE_COLOR = '#ef4444'; // red
/** @graph-connects none */
const PDG_WIP_COLOR = '#a16207'; // darker yellow for in-progress
/** @graph-connects none */
const ACE_WIP_COLOR = '#b91c1c'; // darker red for in-progress

/** @graph-connects none */
export const tooltipStyle = {
  backgroundColor: 'var(--color-bg-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  fontSize: '12px',
  boxShadow: 'var(--shadow-lg)',
};

/** @graph-connects none */
const tooltipWrapperStyle = { zIndex: 50, outline: 'none' };

/** @graph-connects none */
export function actualBarColor(actual: number, target: number, team?: string): string {
  if (actual >= target) return '#16a34a';
  if (team === 'PDG') return PDG_COLOR;
  return '#ef4444';
}

/** @graph-connects none */
function wipBarColor(team?: string): string {
  if (team === 'PDG') return PDG_WIP_COLOR;
  return ACE_WIP_COLOR;
}

/** @graph-connects none */
export function aggregateMembers(
  members: MonthlyCombatPower[],
  monthIdx: number,
): { actual: number; actualInProgress: number; target: number; capacity: number } {
  let actual = 0;
  let actualInProgress = 0;
  let target = 0;
  let capacity = 0;
  for (const m of members) {
    const cm = m.months[monthIdx];
    if (!cm) continue;
    // バックログデータがあればそちらを使用、なければシートにフォールバック
    const bp = cm.backlogPt;
    if (bp) {
      actual += bp.completed;
      actualInProgress += bp.inProgress;
    } else if (cm.actualPower !== null && cm.businessDays > 0) {
      actual += cm.businessDays * cm.actualPower;
    }
    if (cm.expectedPower !== null && cm.businessDays > 0) {
      target += cm.businessDays * cm.expectedPower;
    }
    if (cm.capacity !== null) {
      capacity += cm.capacity;
    }
  }
  return {
    actual: Math.round(actual),
    actualInProgress: Math.round(actualInProgress),
    target: Math.round(target),
    capacity: Math.round(capacity),
  };
}

/** @graph-connects none */
function TargetCompositeBar(props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: ChartItem;
  background?: { height: number };
}) {
  const { x = 0, y = 0, width = 0, height = 0, payload } = props;
  if (!payload) return null;
  const ctRatio = payload.target > 0 ? payload.currentTarget / payload.target : 0;
  const ctHeight = height * ctRatio;
  const ctY = y + height - ctHeight;

  return (
    <g>
      <Rectangle
        x={x}
        y={ctY}
        width={width}
        height={ctHeight}
        fill={CURRENT_TARGET_COLOR}
        radius={[4, 4, 0, 0]}
      />
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill="transparent"
        stroke={TARGET_OUTLINE_COLOR}
        strokeWidth={1.5}
        strokeDasharray="4 3"
        radius={[4, 4, 0, 0]}
      />
    </g>
  );
}

interface BarShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: ChartItem;
}

/** @graph-connects none */
function ActualBar(props: BarShapeProps) {
  const { x = 0, y = 0, width = 0, height = 0, payload } = props;
  if (!payload) return null;
  const totalActual = payload.actual + payload.actualInProgress;
  const fill = actualBarColor(
    totalActual,
    payload.currentTarget,
    payload.teamContext ?? payload.label,
  );
  return <Rectangle x={x} y={y} width={width} height={height} fill={fill} />;
}

/** @graph-connects none */
function WipBar(props: BarShapeProps) {
  const { x = 0, y = 0, width = 0, height = 0, payload } = props;
  if (!payload) return null;
  const totalActual = payload.actual + payload.actualInProgress;
  const isOverTarget = totalActual >= payload.currentTarget;
  const fill = isOverTarget ? '#16a34a' : wipBarColor(payload.teamContext ?? payload.label);
  return <Rectangle x={x} y={y} width={width} height={height} fill={fill} radius={[4, 4, 0, 0]} />;
}

/** @graph-connects none */
function RateLabel(props: {
  x?: number;
  y?: number;
  width?: number;
  index?: number;
  chartData?: ChartItem[];
}) {
  const { x = 0, y = 0, width = 0, index = 0, chartData = [] } = props;
  const entry = chartData[index];
  if (!entry || entry.currentTarget <= 0) return null;
  const totalActual = entry.actual + entry.actualInProgress;
  const rate = (totalActual / entry.currentTarget) * 100;
  const color = actualBarColor(totalActual, entry.currentTarget, entry.teamContext ?? entry.label);
  return (
    <text
      x={x + width / 2}
      y={y - 6}
      fill={color}
      textAnchor="middle"
      fontSize={11}
      fontWeight="bold"
    >
      {`${rate.toFixed(1)}%`}
    </text>
  );
}

/** @graph-connects none */
export function BudgetBarChart({
  items,
  height,
  barSize,
  showXAxis,
  showRate,
  showLegend = true,
}: {
  items: ChartItem[];
  height: number;
  barSize: number;
  showXAxis: boolean;
  showRate?: boolean;
  showLegend?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={items}
        margin={{ top: showRate ? 25 : 10, right: 20, left: 20, bottom: 5 }}
        barGap={8}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        {showXAxis ? (
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} />
        ) : (
          <XAxis dataKey="label" hide />
        )}
        <YAxis
          tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
          tickFormatter={(v: number) => v.toLocaleString()}
        />
        <Tooltip
          cursor={false}
          wrapperStyle={tooltipWrapperStyle}
          contentStyle={tooltipStyle}
          content={({ active, payload: tooltipPayload }) => {
            if (!active || !tooltipPayload?.length) return null;
            const item = tooltipPayload[0]?.payload as ChartItem | undefined;
            if (!item) return null;
            const totalActual = item.actual + item.actualInProgress;
            const completedColor = actualBarColor(
              totalActual,
              item.currentTarget,
              item.teamContext ?? item.label,
            );
            const wipColor =
              totalActual >= item.currentTarget
                ? '#16a34a'
                : wipBarColor(item.teamContext ?? item.label);
            return (
              <div style={tooltipStyle} className="px-3 py-2">
                <p className="font-bold text-[var(--color-text-primary)] mb-1">{item.label}</p>
                <p style={{ color: '#9ca3af' }}>
                  {t('members.combatPower.targetFullLabel')}: {item.target.toLocaleString()}
                </p>
                <p style={{ color: '#6b7280' }}>
                  {t('members.combatPower.currentTargetLabel')}:{' '}
                  {item.currentTarget.toLocaleString()}
                </p>
                <p>
                  <span style={{ color: completedColor }}>
                    {t('members.combatPower.actualPtLabel')}: {item.actual.toLocaleString()}
                  </span>
                  {item.actualInProgress > 0 && (
                    <span style={{ color: wipColor }}>
                      ({item.actualInProgress.toLocaleString()})
                    </span>
                  )}
                </p>
              </div>
            );
          }}
        />
        {showLegend && (
          <Legend
            wrapperStyle={{ fontSize: '12px' }}
            content={() => (
              <ul
                className="recharts-default-legend"
                style={{ padding: 0, margin: 0, textAlign: 'center' }}
              >
                <li
                  className="recharts-legend-item"
                  style={{ display: 'inline-flex', alignItems: 'center', marginRight: 10 }}
                >
                  <svg width={14} height={14} style={{ marginRight: 4 }}>
                    <line
                      x1={0}
                      y1={7}
                      x2={14}
                      y2={7}
                      stroke={TARGET_OUTLINE_COLOR}
                      strokeWidth={2}
                    />
                  </svg>
                  <span style={{ color: 'var(--color-text-muted)' }}>
                    {t('members.combatPower.targetFullLabel')}
                  </span>
                </li>
                <li
                  className="recharts-legend-item"
                  style={{ display: 'inline-flex', alignItems: 'center', marginRight: 10 }}
                >
                  <svg width={14} height={14} style={{ marginRight: 4 }}>
                    <rect width={14} height={14} fill={CURRENT_TARGET_COLOR} />
                  </svg>
                  <span style={{ color: 'var(--color-text-muted)' }}>
                    {t('members.combatPower.currentTargetLabel')}
                  </span>
                </li>
              </ul>
            )}
          />
        )}
        <Bar
          dataKey="target"
          name={t('members.combatPower.targetFullLabel')}
          stackId="target"
          barSize={barSize}
          shape={<TargetCompositeBar />}
          legendType="none"
        />
        <Bar
          dataKey="actual"
          name={t('members.combatPower.actualPtLabel')}
          stackId="actual"
          barSize={barSize}
          shape={<ActualBar />}
          legendType="none"
        />
        <Bar
          dataKey="actualInProgress"
          name={t('members.combatPower.wipLabel')}
          stackId="actual"
          barSize={barSize}
          shape={<WipBar />}
          legendType="none"
        >
          {showRate && <LabelList content={<RateLabel chartData={items} />} />}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
